import { useEffect, useMemo, useState } from "react";
import { Globe, Link2, PlugZap, Power, Plus, RefreshCw, Trash2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";

const API_BASE_AFFILIATE = "https://api.sobevenda.com.br/api-proxy/affiliate";
const API_BASE_SOBEPAGES = "https://api.sobevenda.com.br/api-proxy/sobepages";

function getPlanLabel(plan) {
  if (!plan) return "Start";
  return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
}

function getToken() {
  return localStorage.getItem("sobevenda_token") || "";
}

function formatDate(ts) {
  if (!ts) return "-";
  let date;
  if (typeof ts === "number") {
    const ms = ts < 10_000_000_000 ? ts * 1000 : ts;
    date = new Date(ms);
  } else {
    date = new Date(ts);
  }
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR");
}

async function apiAffiliate(path, options = {}) {
  const res = await fetch(`${API_BASE_AFFILIATE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) throw new Error(data?.message || data?.error || "Erro na requisição.");
  return data;
}

async function apiSobePages(path, options = {}) {
  const res = await fetch(`${API_BASE_SOBEPAGES}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    if (res.status === 400 && data?.payload) {
      throw new Error(data.payload.message || data?.message || "Erro na requisição.");
    }
    throw new Error(data?.message || data?.error || "Erro na requisição.");
  }
  return data;
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status, sslStatus }) {
  const isConnected = status === "connected" || status === "active" || status === "verified" || sslStatus === "active";

  if (isConnected) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
        <CheckCircle2 className="w-3.5 h-3.5" />
        {status === "verified" ? "Verificado" : "Conectado"}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Erro
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      Pendente
    </span>
  );
}

function SslBadge({ sslStatus }) {
  if (sslStatus === "active") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Ativo
      </span>
    );
  }
  if (sslStatus === "pending_validation" || sslStatus === "initializing") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {sslStatus === "initializing" ? "Iniciando" : "Validando"}
      </span>
    );
  }
  if (!sslStatus) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        —
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      {sslStatus}
    </span>
  );
}

// ─── Modal: Adicionar Domínio ─────────────────────────────────────────────────

function AddDomainModal({ isOpen, onClose, onSuccess }) {
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) setNewDomain("");
  }, [isOpen]);

  async function handleAdd() {
    if (!newDomain.trim()) {
      toast.error("Digite um domínio válido.");
      return;
    }

    let domain = newDomain.trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "")
      .toLowerCase();

    if (domain.startsWith("www.")) domain = domain.replace("www.", "");

    const parts = domain.split(".");
    if (parts.length < 2) {
      toast.error("Domínio inválido. Use: seudominio.com");
      return;
    }
    if (domain.includes(" ")) {
      toast.error("Domínio não pode conter espaços.");
      return;
    }

    setLoading(true);
    try {
      await apiSobePages("/domains", {
        method: "POST",
        body: JSON.stringify({ domain }),
      });
      toast.success("Domínio adicionado com sucesso!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.message || "Erro ao adicionar domínio.");
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} />

      <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-lg">
        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">
          Adicionar domínio
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
          Digite o domínio principal (sem www). O sistema configura o Cloudflare automaticamente.
        </p>

        <div className="mb-4">
          <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Domínio</label>
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="seudominio.com"
            disabled={loading}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 disabled:opacity-50"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Exemplos: seudominio.com · minhaloja.com.br · vendas.online
          </p>
        </div>

        <div className="mb-5 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-sm text-slate-600 dark:text-slate-300">
          Após adicionar, você receberá os <strong>nameservers do Cloudflare</strong> para configurar no seu registrador de domínios. A ativação pode levar de 5 minutos até 48 horas.
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleAdd}
            disabled={loading || !newDomain.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {loading ? "Salvando..." : "Adicionar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Instruções Nameservers ────────────────────────────────────────────

function NameserversModal({ domain, onClose }) {
  if (!domain) return null;

  function copyToClipboard(value) {
    navigator.clipboard.writeText(value);
    toast.success("Copiado!");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-lg">
        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">
          Zona criada no Cloudflare
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
          Configure os nameservers abaixo no painel do seu registrador (GoDaddy, Registro.br, Hostinger, etc) para ativar o domínio.
        </p>

        <div className="space-y-3 mb-5">
          {[
            { label: "Nameserver 1", value: domain.nameserver_1 },
            { label: "Nameserver 2", value: domain.nameserver_2 },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  {label}
                </span>
                <button
                  onClick={() => copyToClipboard(value)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copiar
                </button>
              </div>
              <code className="block font-mono text-sm text-slate-900 dark:text-slate-100 break-all">
                {value || "Carregando..."}
              </code>
            </div>
          ))}
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
          A ativação pode levar de <strong>5 minutos até 48 horas</strong>. Geralmente ativa em 30 minutos. Use o botão <strong>"Verificar status"</strong> para acompanhar.
        </p>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [user, setUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNameserversModal, setShowNameserversModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const connectedCount = useMemo(
    () => domains.filter((d) => d.is_active || d.status === "connected" || d.status === "active").length,
    [domains]
  );

  const maxDomains = user?.max_connected_domains ?? null;
  const reachedLimit = maxDomains !== null && connectedCount >= maxDomains;

  async function loadUser() {
    try {
      const me = await apiAffiliate("/auth/me", { method: "GET" });
      setUser(me);
    } catch {
      setUser(null);
    }
  }

  // Retorna a lista atualizada de domínios E atualiza o estado
  async function loadDomains() {
    setLoading(true);
    try {
      const data = await apiSobePages("/domains", { method: "GET" });
      const list = Array.isArray(data) ? data : [];
      setDomains(list);
      return list; // ← retorna para quem precisar usar imediatamente
    } catch {
      toast.error("Não foi possível carregar os domínios.");
      setDomains([]);
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function handleRefreshAll() {
    setLoading(true);
    try {
      // Verifica no Cloudflare todos os domínios que ainda não estão ativos
      const pending = domains.filter((d) => !d.is_active && d.cloudflare_zone_id);
      await Promise.all(
        pending.map((d) =>
          apiSobePages(`/domains/${d.id}/check-status`, { method: "GET" }).catch(() => {})
        )
      );
    } finally {
      await loadDomains();
    }
  }

  useEffect(() => {
    loadUser();
    loadDomains();
  }, []);

  // Auto-refresh para domínios com SSL pendente
  useEffect(() => {
    const hasPending = domains.some(
      (d) => d.ssl_status === "pending_validation" || d.ssl_status === "initializing"
    );
    if (!hasPending) return;

    const interval = setInterval(() => {
      domains.forEach((domain) => {
        if (domain.ssl_status === "pending_validation" || domain.ssl_status === "initializing") {
          apiSobePages(`/domains/${domain.id}/check-status`, { method: "GET" })
            .then((status) => {
              if (status.is_active) {
                toast.success(`SSL ativado para ${domain.domain}!`);
              }
              loadDomains();
            })
            .catch(() => {});
        }
      });
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains]);

  async function handleConnect(domainId) {
    setActionLoadingId(domainId);
    try {
      // 1. Chama o endpoint de conexão
      await apiSobePages(`/domains/${domainId}/connect`, {
        method: "POST",
        body: JSON.stringify({ domains_id: domainId }),
      });

      // 2. Recarrega a lista e usa o retorno direto da API (não o estado antigo)
      //    Isso resolve o bug dos nameservers não aparecerem no primeiro clique.
      const updatedList = await loadDomains();
      const freshDomain = updatedList.find((d) => d.id === domainId);

      // 3. Abre o modal de nameservers com o dado fresco da API
      if (freshDomain) {
        setSelectedDomain(freshDomain);
        setShowNameserversModal(true);
      }

      toast.success("Domínio conectado com sucesso!");

      // 4. Dispara o check-status automaticamente após 3s para já tentar verificar
      //    Isso resolve o domínio ficar preso em "Pendente" sem o usuário clicar.
      setTimeout(() => {
        apiSobePages(`/domains/${domainId}/check-status`, { method: "GET" })
          .then(() => loadDomains())
          .catch(() => {});
      }, 3000);

    } catch (error) {
      toast.error(error.message || "Erro ao conectar domínio.");
    } finally {
      setActionLoadingId(null);
    }
  }

  async function handleCheckStatus(domainId) {
    setActionLoadingId(domainId);
    try {
      const status = await apiSobePages(`/domains/${domainId}/check-status`, { method: "GET" });
      await loadDomains();
      if (status.is_active) {
        toast.success(status.message || "Domínio ativo e funcionando!");
      } else if (status.ssl_status === "pending_validation") {
        toast.info("DNS configurado. Aguardando validação SSL...");
      } else if (status.hostname_status === "pending") {
        toast.info("Aguardando configuração DNS...");
      } else {
        toast.info(status.message || "Status verificado.");
      }
    } catch (error) {
      const msg = error.message || "Erro ao verificar status.";
      if (msg.includes("não foi conectado")) {
        toast.warning("Domínio ainda não conectado ao Cloudflare. Clique em Conectar primeiro.");
      } else {
        toast.error(msg);
      }
    } finally {
      setActionLoadingId(null);
    }
  }

  async function handleDisconnect(domainId) {
    setActionLoadingId(domainId);
    try {
      await apiSobePages(`/domains/${domainId}/disconnect`, {
        method: "POST",
        body: JSON.stringify({ domains_id: domainId }),
      });
      toast.success("Domínio desconectado.");
      await loadDomains();
    } catch (error) {
      toast.error(error.message || "Erro ao desconectar domínio.");
    } finally {
      setActionLoadingId(null);
    }
  }

  async function handleDelete() {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    setActionLoadingId(id);
    try {
      await apiSobePages(`/domains/${id}`, { method: "DELETE" });
      toast.success("Domínio excluído com sucesso.");
      await loadDomains();
    } catch (error) {
      toast.error(error.message || "Erro ao excluir domínio.");
    } finally {
      setActionLoadingId(null);
    }
  }

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Globe className="w-6 h-6" />
              Domínios
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Conecte o domínio que será usado para publicar suas páginas no SobeVenda.
            </p>
          </div>

          <button
            onClick={handleRefreshAll}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* KPIs */}
        <section className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Plano atual
            </p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
              {user ? getPlanLabel(user.plan) : "-"}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Domínios conectados
            </p>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-slate-100">
                {connectedCount}
              </span>
              <span className="pb-1 text-sm text-slate-500 dark:text-slate-400">
                {maxDomains !== null ? `/ ${maxDomains}` : ""}
              </span>
            </div>
            {reachedLimit && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Limite do plano atingido.
              </p>
            )}
          </div>
        </section>

        {/* AÇÃO PRIMÁRIA */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Plus className="w-4 h-4" />
            Adicionar domínio
          </button>
        </div>

        {/* LISTAGEM — CARDS NO MOBILE / TABELA NO DESKTOP */}

        {/* Mobile/Tablet: Cards (visível < lg) */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm">
              Carregando domínios...
            </div>
          ) : domains.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Nenhum domínio conectado ainda.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                <Plus className="w-4 h-4" />
                Adicionar domínio
              </button>
            </div>
          ) : (
            domains.map((domain) => {
              const busy = actionLoadingId === domain.id;
              const connected = domain.status === "connected" || domain.status === "active" || domain.is_active || domain.ssl_status === "active";

              return (
                <div key={domain.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Domínio</div>
                      <div className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100 break-all">
                        {domain.domain}
                      </div>
                    </div>
                    <StatusBadge status={domain.status} sslStatus={domain.ssl_status} />
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">SSL</div>
                      <SslBadge sslStatus={domain.ssl_status} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Conectado em</div>
                      <div className="text-slate-600 dark:text-slate-300 text-xs">{formatDate(domain.connected_at)}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    {!connected ? (
                      <button
                        onClick={() => handleConnect(domain.id)}
                        disabled={busy}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                      >
                        {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
                        Conectar
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleCheckStatus(domain.id)}
                          disabled={busy}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                        >
                          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          Verificar
                        </button>
                        <button
                          onClick={() => handleDisconnect(domain.id)}
                          disabled={busy}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                        >
                          <Power className="w-4 h-4" />
                          Desconectar
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setConfirmDeleteId(domain.id)}
                      disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Excluir
                    </button>
                  </div>

                  <div className="text-[11px] text-slate-400 text-right mt-2">ID: {domain.id}</div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop: Tabela (visível >= lg) */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr className="text-left">
                  <th className="px-4 py-3 w-[28%] font-semibold text-slate-700 dark:text-slate-200">Domínio</th>
                  <th className="px-4 py-3 w-[12%] font-semibold text-slate-700 dark:text-slate-200">Status</th>
                  <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">SSL</th>
                  <th className="px-4 py-3 w-[15%] font-semibold text-slate-700 dark:text-slate-200">Conectado em</th>
                  <th className="px-4 py-3 w-[35%] text-right font-semibold text-slate-700 dark:text-slate-200">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-4 text-slate-500 text-sm" colSpan={5}>Carregando domínios...</td>
                  </tr>
                ) : domains.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-sm" colSpan={5}>
                      <div className="text-center">
                        <p className="text-slate-500 mb-4">Nenhum domínio conectado ainda.</p>
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar domínio
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  domains.map((domain) => {
                    const busy = actionLoadingId === domain.id;
                    const connected = domain.status === "connected" || domain.status === "active" || domain.is_active || domain.ssl_status === "active";

                    return (
                      <tr key={domain.id} className="border-t border-slate-100 dark:border-slate-800 align-middle">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" />
                            <div className="min-w-0">
                              <div className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100 break-all">
                                {domain.domain}
                              </div>
                              <div className="text-xs text-slate-400">ID #{domain.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={domain.status} sslStatus={domain.ssl_status} />
                        </td>
                        <td className="px-4 py-3">
                          <SslBadge sslStatus={domain.ssl_status} />
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                          {formatDate(domain.connected_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {!connected ? (
                              <button
                                onClick={() => handleConnect(domain.id)}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                              >
                                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
                                Conectar
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleCheckStatus(domain.id)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                                >
                                  {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                  Verificar status
                                </button>
                                <button
                                  onClick={() => handleDisconnect(domain.id)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
                                >
                                  <Power className="w-4 h-4" />
                                  Desconectar
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => setConfirmDeleteId(domain.id)}
                              disabled={busy}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* MODAIS */}
      <AddDomainModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadDomains}
      />

      {showNameserversModal && (
        <NameserversModal
          domain={selectedDomain}
          onClose={() => {
            setShowNameserversModal(false);
            setSelectedDomain(null);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Excluir domínio"
        message="Tem certeza que deseja excluir este domínio? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}